import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useLocation } from '@reach/router';

import useFetch from '../utils/useFetch';
import { callApi } from '../../actions/base';
import {HTTP_METHOD} from "../../constants/http";

const mapStatuses = {
  new: 'Utworzono',
  in_progress: 'W trakcie',
  manual_verification_pending: 'W trakcie weryfikacji',
  completed: 'W trakcie weryfikacji',
  accepted: 'Zaakceptowano',
  rejected: 'Odrzucono',
};

const mapTypes = {
  individual: 'Osoba fizyczna',
  sole_proprietorship: 'Jednoosobowa działalność gospodarcza',
  company: 'Spółka lub fundacja',
};

const mapVerMethods = {
  fiberpay_transfer: 'Przelew weryfikacyjny',
  banqup: 'Dane konta bankowego',
};

const BasicTable = ({ order }) => {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const location = useLocation();

  const handleCopied = () => setShowSnackbar(true);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowSnackbar(false);
  };

  const copyUrl = `${location.origin}/orders/${order.code}`;

  return (
    <>
      <TableContainer>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Utworzono</TableCell>
              <TableCell>Zaktualizowano</TableCell>
              <TableCell>Metoda weryfikacji tożsamości</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                {mapStatuses[order.status]}
              </TableCell>
              <TableCell>{mapTypes[order.type]}</TableCell>
              <TableCell>{order.createdAt}</TableCell>
              <TableCell>{order.updatedAt}</TableCell>
              <TableCell>
                {mapVerMethods[order.identityVerificationMethod] || <i>brak</i>}
              </TableCell>
              <TableCell>
                <Tooltip title="Skopiuj do schowka">
                  <CopyToClipboard text={copyUrl} onCopy={handleCopied}>
                    <Button
                      color="primary"
                      variant="outlined"
                      disableElevation
                      endIcon={<FileCopyOutlinedIcon />}
                    >
                      Link
                    </Button>
                  </CopyToClipboard>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="success">
          Skopiowano do schowka
        </Alert>
      </Snackbar>
    </>
  );
};

const DetailsTable = ({ order }) => {
  const entries = Object.values(order.formAnswers);

  const handleSuccess = (data) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    const filename = `zlecenie_weryfikacyjne_${order.code}-raport_${new Date().toISOString()}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // noinspection JSIgnoredPromiseFromCall
  const handleGetReport = () => {
    const uri = `/auth/orders/${order.code}/reports`;
    const additionalOptions = { responseType: 'blob' };
    callApi(uri, null, handleSuccess, console.error, HTTP_METHOD.GET, additionalOptions);
  };

  return (
    <>
      <Toolbar disableGutters>
        <Typography variant="h6" component="div" style={{ flexGrow: 1 }}>
          Zebrane dane
        </Typography>
        <Button
          endIcon={<GetAppIcon />}
          variant="contained"
          color="primary"
          disableElevation
          onClick={handleGetReport}
        >
          Pobierz raport(PDF)
        </Button>
      </Toolbar>
      <TableContainer>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Nazwa </TableCell>
              <TableCell>Wartość</TableCell>
              {/* <TableCell>Źródło</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(
              ({
                answer,
                question,
              }) => answer !== null
                && (Array.isArray(answer) ? (
                  <>
                    {answer.map((a) => (
                      <TableRow key={a}>
                        <TableCell>{question.content_pl}</TableCell>
                        <TableCell>{`${a.toUpperCase()}`}</TableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  <TableRow key={question.content_pl}>
                    <TableCell>{question.content_pl}</TableCell>
                    <TableCell>{`${(`${answer}`).toUpperCase()}`}</TableCell>
                  </TableRow>
                )),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const OrderDetailsPanel = ({ code }) => {
  const uri = `/auth/orders/${code}`;
  const {
    resource: order,
    loading,
    error,
  } = useFetch(uri, null, HTTP_METHOD.GET);

  if (error) return <Alert severity="error">{error}</Alert>;

  if (loading) {
    return (
      <Box textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      {order && (
        <>
          <Box pb={2} textAlign="center">
            <Typography variant="h5" paragraph>
              Zlecenie #
              {order.code}
            </Typography>
          </Box>
          <BasicTable order={order} />
          {order.status === 'accepted' && (
            <Box mt={4}>
              <DetailsTable order={order} />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default OrderDetailsPanel;
